import type { FunctionComponent } from 'react';
import { AlertCircle, AlertTriangle, Info } from 'lucide-react';

type RiskIndicatorsProps = {
  indicators?: Array<Record<string, unknown>>;
};

const getSeverityIcon = (severity: string) => {
  switch (severity) {
    case 'critical':
    case 'high':
      return <AlertTriangle className="h-5 w-5 text-red-500" />;
    case 'medium':
      return <AlertCircle className="h-5 w-5 text-orange-500" />;
    default:
      return <Info className="h-5 w-5 text-blue-500" />;
  }
};

const getSeverityStyles = (severity: string) => {
  switch (severity) {
    case 'critical':
      return 'border-l-red-600 bg-red-50';
    case 'high':
      return 'border-l-orange-500 bg-orange-50';
    case 'medium':
      return 'border-l-yellow-500 bg-yellow-50';
    default:
      return 'border-l-blue-500 bg-blue-50';
  }
};

export const RiskIndicators: FunctionComponent<RiskIndicatorsProps> = ({ indicators }) => {
  if (!indicators || indicators.length === 0) {
    return null;
  }

  return (
    <div className="rounded-lg border bg-white p-6 shadow-sm">
      <h2 className="text-xl font-semibold" style={{ color: '#14203D' }}>
        Risk Indicators
      </h2>

      <div className="mt-4 space-y-3">
        {indicators.map((indicator, index) => (
          <div
            key={index}
            className={`rounded-r-lg border-l-4 p-4 ${getSeverityStyles(indicator.severity as string)}`}
          >
            <div className="flex items-start gap-3">
              {getSeverityIcon(indicator.severity as string)}
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium text-gray-900">{indicator.indicator as string}</h3>
                  <span className="text-xs font-medium uppercase text-gray-500">
                    {indicator.category as string}
                  </span>
                </div>
                <p className="mt-1 text-sm text-gray-600">{indicator.description as string}</p>
                {(indicator.evidence as string[])?.length > 0 && (
                  <ul className="mt-2 list-inside list-disc text-xs text-gray-500">
                    {(indicator.evidence as string[]).slice(0, 2).map((evidence, idx) => (
                      <li key={idx}>{evidence}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
